import { useState, useEffect, useRef } from 'react';
import {
  Zap,
  Users,
  Target,
  Lightbulb,
  Award,
  Globe,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "Advancing drone technology for real-world applications and societal benefit"
    },
    {
      icon: Users,
      title: "Collaborative",
      description: "Building a diverse community of engineers, researchers, and innovators"
    },
    {
      icon: Lightbulb,
      title: "Innovation-Focused",
      description: "Pushing boundaries in autonomous systems, AI navigation, and swarm intelligence"
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Contributing to solutions for environmental monitoring, disaster response, and more"
    }
  ];

  const achievements = [
    "#1 Tech Society at NSUT EAST",
    "50+ successful research projects completed",
    "Published research in top-tier journals",
    "Industry partnerships with leading tech companies",
    "Student mentorship sessions by top industry professionals"
  ];

  return (
    <section ref={sectionRef} className="relative py-24 bg-slate-950 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-drone-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-300 backdrop-blur-sm mb-6">
            <Zap className="h-4 w-4" />
            About Our Society
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Shaping the Future of
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"> Aerial Technology</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We are a passionate community of innovators, researchers, and engineers dedicated to advancing
            drone technology and its applications for a better tomorrow.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Story */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}>
            <h3 className="text-2xl font-bold text-white mb-6">Our Story</h3>
            <div className="space-y-4 text-gray-300">
              <p>
                Founded in 2024, the Aeronaut Drone Society began as a small group of engineering
                students passionate about unmanned aerial vehicles. What started as weekend projects
                has evolved into a leading research organization.
              </p>
              <p>
                Today, we're at the forefront of drone innovation, working on cutting-edge projects
                that span autonomous navigation, swarm intelligence, environmental monitoring, and
                emergency response systems.
              </p>
              <p>
                Alongside our drone projects, we build software that empowers creators and innovators.              
                </p>
            </div>

            {/* Achievements List */}
            <div className="mt-8">
              <h4 className="text-lg font-semibold text-white mb-4">Key Achievements</h4>
              <div className="space-y-3">
                {achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>

            <button className="mt-8 group flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 text-white font-semibold transition-all hover:scale-105">
              Learn More About Us
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* Right Column - Features Grid */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all hover:bg-white/10 hover:border-blue-500/30"
                >
                  <div className="mb-4">
                    <div className="inline-flex p-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">{feature.title}</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Stats Card */}
            <div className="mt-8 p-6 rounded-2xl border border-white/10 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-white">150+</div>
                  <div className="text-sm text-gray-300">Members</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">50+</div>
                  <div className="text-sm text-gray-300">Projects</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">2</div>
                  <div className="text-sm text-gray-300">Years</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;